/* eslint-disable jsx-a11y/control-has-associated-label */
import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames/bind";
// eslint-disable-next-line import/no-cycle
import { Button } from "../Button";
import { Img } from "../../sharedComponents/Images";
import { Icon } from "../Icons";
import { Cell, Row } from "../Grid";
import { Heading } from "../Headings/Heading";

import * as styles from "./SocialAndAppsSection.module.scss";

const cx = classNames.bind(styles);

const AppsLink = ({ href, target, title, img, className }) => (
	<a href={href} target={target} title={title} className={cx("link")}>
		<Img
			fixed={img.fixed}
			svg={img.svg}
			wrapperClassName={className}
			alt={img.title ? img.title : ""}
		/>
	</a>
);
const SocialLink = ({ href, target, title, icon }) => (
	<a href={href} target={target} title={title} className={cx("link", "socialLink", icon)}>
		<Icon name={icon} />
	</a>
);
const SocialAndAppsSection = ({
	appIcon,
	appsTitle,
	appsSubTitle,
	appsDescription,
	appsLinks,
	appsButtons,
	socialTitle,
	socialSubTitle,
	socialDescription,
	socialLinks,
	socialButtons,
	socialIcon,
	socialImage,
	variant,
}) => (
	<>
		{variant === "primary" && (
			<Row cols="social">
				<Cell>
					<div className={styles.share}>
						{socialImage && (
							<div className={styles.shareImage}>
								<Img
									src={!socialImage.srcSet && socialImage.src}
									source={socialImage.srcSet && socialImage}
									fluid={socialImage.fluid}
									alt={socialImage.title ? socialImage.title : ""}
								/>
							</div>
						)}
						<div className={styles.shareContent}>
							<Heading as="h3" size="h4">
								{socialTitle}
							</Heading>
							<div className={styles.linksContainer}>
								{socialLinks?.map(
									link =>
										link.id && (
											<SocialLink
												key={link.id}
												href={link.link.url}
												target={link.link.target}
												title={link.title}
												icon={link.iconName}
												className={styles.appsImages}
											/>
										)
								)}
							</div>
						</div>
					</div>
				</Cell>
				<Cell>
					<div className={styles.download}>
						<div className={styles.socialHeadContent}>
							<Heading as="h3" size="h4" className={styles.socialHeadTitle}>
								{appsTitle}
							</Heading>
						</div>
						<div className={styles.linksContainer}>
							{appsLinks?.map(
								link =>
									link.id && (
										<AppsLink
											key={link.id}
											href={link.link.url}
											target={link.link.target}
											title={link.title}
											img={link.icon.img}
											className={styles.appsImages}
										/>
									)
							)}
						</div>
					</div>
				</Cell>
			</Row>
		)}
		{variant === "secondary" && (
			<Row cols={2}>
				<Cell>
					<div className={cx("section", "social", variant)}>
						<div className={cx("socialHead", !appIcon ? "socialHeadWithIcon" : "")}>
							{socialIcon && (
								<Icon name={socialIcon} className={`swg-text-6xl ${styles.socialHeadIcon}`} />
							)}
							<div className={styles.socialHeadContent}>
								<Heading as="h3" size="h4" className={styles.socialHeadTitle}>
									{socialTitle}
								</Heading>
								<div>{socialSubTitle}</div>
							</div>
						</div>
						<div className={styles.socialContent}>
							<div className={cx("description", "socialContentDesc")}>{socialDescription}</div>
							<div className={styles.linksContainer}>
								{socialButtons?.map(
									button =>
										button.id && (
											<div>
												<Button
													key={button.id}
													className={styles.button}
													outline={button.invertButtonStyle}
													to={button.link && button.link}
													icon={button.iconName}>
													{button.text && button.text}
												</Button>
											</div>
										)
								)}
							</div>
						</div>
					</div>
				</Cell>
				<Cell>
					<div className={cx("section", variant)}>
						<div className={cx("socialHead", !appIcon ? "socialHeadWithIcon" : "")}>
							{appIcon && (
								<Icon name={appIcon} className={`swg-text-6xl ${styles.socialHeadIcon}`} />
							)}
							<div className={styles.socialHeadContent}>
								<Heading as="h3" size="h4" className={styles.socialHeadTitle}>
									{appsTitle}
								</Heading>
								<div>{appsSubTitle}</div>
							</div>
						</div>
						<div className={styles.socialContent}>
							<div className={styles.socialContentDesc}>{appsDescription}</div>
							<div className={styles.linksContainer}>
								{appsButtons?.map(
									button =>
										button.id && (
											<div>
												<Button
													key={button.id}
													outline={button.invertButtonStyle}
													to={button.link && button.link}
													className={styles.button}
													icon={button.iconName}>
													{button.text && button.text}
												</Button>
											</div>
										)
								)}
							</div>
						</div>
					</div>
				</Cell>
			</Row>
		)}
	</>
);

SocialAndAppsSection.propTypes = {
	appIcon: PropTypes.string,
	socialIcon: PropTypes.string,
	appsButtons: PropTypes.arrayOf(PropTypes.object),
	appsTitle: PropTypes.string.isRequired,
	appsSubTitle: PropTypes.string,
	appsDescription: PropTypes.string,
	appsLinks: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.string,
			link: PropTypes.shape({
				url: PropTypes.string,
				linkText: PropTypes.string,
				target: PropTypes.string,
			}),
			icon: PropTypes.shape({
				img: PropTypes.shape({
					title: PropTypes.string,
					fluid: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
					fixed: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
					svg: PropTypes.shape({
						dataURI: PropTypes.string,
					}),
				}),
				icon: PropTypes.string,
			}),
		})
	).isRequired,
	socialTitle: PropTypes.string.isRequired,
	socialSubTitle: PropTypes.string,
	socialDescription: PropTypes.string,
	variant: PropTypes.oneOf(["primary", "secondary"]),
	socialButtons: PropTypes.arrayOf(PropTypes.object),
	socialLinks: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.string,
			link: PropTypes.shape({
				url: PropTypes.string,
				linkText: PropTypes.string,
				target: PropTypes.string,
			}),
			iconName: PropTypes.string,
		})
	).isRequired,
	socialImage: PropTypes.shape({
		fluid: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
		fixed: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
		source: PropTypes.shape({
			src: PropTypes.string,
			srcSet: PropTypes.string,
			sizes: PropTypes.string,
		}),
		title: PropTypes.string,
		src: PropTypes.string,
		srcSet: PropTypes.string,
	}).isRequired,
};
AppsLink.propTypes = {
	href: PropTypes.string.isRequired,
	target: PropTypes.string.isRequired,
	title: PropTypes.string.isRequired,
	img: PropTypes.shape({
		fluid: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
		fixed: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
		title: PropTypes.string,
		svg: PropTypes.shape({
			dataURI: PropTypes.string,
		}),
	}).isRequired,

	className: PropTypes.string.isRequired,
};
SocialLink.propTypes = {
	href: PropTypes.string.isRequired,
	target: PropTypes.string.isRequired,
	title: PropTypes.string.isRequired,
	icon: PropTypes.string.isRequired,
};
SocialAndAppsSection.defaultProps = {
	socialButtons: null,
	appsButtons: null,
	socialSubTitle: null,
	socialDescription: null,
	appsSubTitle: null,
	appsDescription: null,
	variant: "primary",
	appIcon: null,
	socialIcon: null,
};

export default SocialAndAppsSection;
export { SocialAndAppsSection };
